import { motion } from "framer-motion";
import {
  Lightbulb,
  Sparkles,
  LayoutPanelTop,
  Grid3x3,
  Anchor,
  PenTool,
  TerminalSquare,
  MonitorPlay,
} from "lucide-react";
import { SERVICES } from "@/data/content";
import ScrambleText from "@/components/ScrambleText";

const ICONS = {
  Lightbulb,
  Sparkles,
  LayoutPanelTop,
  Grid3x3,
  Anchor,
  PenTool,
  TerminalSquare,
  MonitorPlay,
};

const SectionHeading = ({ overline, title }) => (
  <div className="mb-16 sm:mb-20">
    <motion.span
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="mb-5 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-zinc-500"
    >
      <span className="h-px w-10 bg-zinc-600" />
      <ScrambleText text={overline} as="span" />
    </motion.span>
    <motion.h2
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white max-w-3xl"
    >
      <ScrambleText text={title} as="span" delay={150} />
    </motion.h2>
  </div>
);

export default function Services() {
  return (
    <section
      id="services"
      className="relative bg-black px-5 sm:px-8 lg:px-12 py-24 sm:py-32"
      data-testid="services"
    >
      <div className="mx-auto max-w-7xl">
        <SectionHeading
          overline="What We Do"
          title="Eight disciplines. One immersive vision."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <motion.div
                key={s.id}
                data-testid={`service-${s.id}`}
                data-cursor="hover"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
                className="group relative bg-[#050505] p-8 min-h-[260px] flex flex-col justify-between transition-colors duration-500 hover:bg-[#0c0c0e]"
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.10) 0%, transparent 60%)",
                  }}
                />
                <div className="relative flex items-start justify-between">
                  <span className="font-display text-xs font-bold tracking-[0.2em] text-zinc-600">
                    {s.index}
                  </span>
                  <Icon
                    className="h-6 w-6 text-zinc-500 transition-all duration-500 group-hover:text-white group-hover:-translate-y-1"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="relative">
                  <h3 className="font-display text-xl font-semibold text-white leading-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm font-light leading-relaxed text-zinc-500 opacity-0 max-h-0 overflow-hidden transition-all duration-500 group-hover:opacity-100 group-hover:max-h-40 group-hover:mt-3">
                    {s.blurb}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
